import React, { FC, useEffect, useState } from 'react';

import { RuleRead } from '@ariksa/compliance-policies';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { find, isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { useAlertService } from 'services/Notification/useAlerts';
import { customTheme } from 'theme';

import { DetailsIdentifier, StackedHeader } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { severityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import { CancelButton, PrimaryButton, Select } from 'components/DataEntry';
import { SeverityIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectPolicyHub } from 'containers/PolicyHub/selectors';
import { actions } from 'containers/PolicyHub/slice';
import { toTitleCase } from 'utils/string';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  policy: RuleRead;
}

export const ChangeSeverityForPolicy: FC<Props> = props => {
  const { isOpen, onClose, policy } = props;
  const { getCloudNativeName } = useResourceType();
  const [newSeverity, setNewSeverity] = useState(severityOptions[0]);
  const dispatch = useDispatch();
  const { policyAction } = useSelector(selectPolicyHub);
  const { alertsByRuleIdSummary } = useAlertService();

  useDeepCompareEffect(() => {
    !!policy &&
      alertsByRuleIdSummary.sync({
        ruleIdSummaryRequest: {
          rule_ids: [policy.config_id],
        },
      });
  }, [alertsByRuleIdSummary, policy]);

  useEffect(() => {
    setNewSeverity(
      find(
        severityOptions,
        o => o?.value === policy?.severity?.toLowerCase(),
      ) ?? severityOptions[0],
    );
  }, [policy]);

  const handleUpdateSeverity = () => {
    dispatch(
      actions.changeSeverityForPolicy({
        q: {
          severityUpdateItem: [
            { policy_id: policy.id, severity: toTitleCase(newSeverity?.value) },
          ],
        },
        onSuccess: () => {
          onClose();
        },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          icon={<SeverityIcon />}
          upper="Change Severity for Policy"
          lower={'Policy ID: ' + policy?.config_id}
          bg={customTheme.colors.orange[300]}
        />
      }
      body={
        <Stack spacing={4}>
          <DetailsIdentifier
            label="Resource Type"
            value={getCloudNativeName(policy?.resource_type!)}
          />
          <DetailsIdentifier
            label="Policy Description"
            value={
              <Box
                px={4}
                py={3}
                border="1px solid"
                borderColor="componentBorder"
                borderRadius={6}
              >
                {policy.compliance_description}
              </Box>
            }
            isInline={false}
            showColon={false}
          />
          <DetailsIdentifier
            label="New Severity"
            value={
              <Stack>
                <Box w={64}>
                  <Select
                    value={newSeverity}
                    options={severityOptions}
                    onChange={opt => {
                      setNewSeverity?.(opt);
                    }}
                    menuPortalTarget={document.body}
                    showClearIndicator={false}
                  />
                </Box>
                <Box color="critical" fontSize="sm" pl={3}>
                  Ariksa Severity: {toTitleCase(policy.default_severity!)}
                </Box>
              </Stack>
            }
            pt={1}
          />
          {/*<DetailsIdentifier label="Blueprints With This Policy" value={map(policy.b)} isInline={false} />*/}
          <Box borderRadius={6} px={4} py={3} bg={customTheme.colors.blue[100]}>
            There are currently{' '}
            {alertsByRuleIdSummary.isLoading ? (
              <Box display="inline-block">
                <CustomSpinner />
              </Box>
            ) : (
              alertsByRuleIdSummary.data?.[0]?.alert_count
            )}{' '}
            policy violations or issues for this policy. Severity will be
            updated for this policy in all blueprints when these issues are
            re-evaluated.
          </Box>
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={() => onClose()}>CANCEL</CancelButton>
          <PrimaryButton
            px={6}
            onClick={handleUpdateSeverity}
            isLoading={policyAction.isLoading}
            isDisabled={policyAction.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
      size="2xl"
    />
  );
};
